import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import { memo, useCallback, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "stores";
import { appStateMutate } from "stores/modules/app.state/operations";
import { getAppState } from "stores/modules/app.state/selectors";
import { RoomVariable } from "stores/modules/entities.rooms";
import { updateCurrentRoom } from "stores/modules/entities.rooms/operations";
import { getCurrentRoom } from "stores/modules/entities.rooms/selectors";
import styled from "styled-components";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  TextFieldSafeMouseSensor,
  TextFieldSafeTouchSensor,
} from "modules/draggableSensor";

const MAX_VARIABLES = 16;

const RoomVariablesDialog = () => {
  const open = useAppSelector((state) =>
    getAppState(state, "openRoomVariables")
  );
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const {
    control,
    register,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      roomVariables: [] as RoomVariable[],
    },
  });
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "roomVariables",
  });

  const sensors = useSensors(
    useSensor(TextFieldSafeMouseSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(TextFieldSafeTouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 2,
      },
    })
  );

  const variables = useAppSelector(
    (state) => getCurrentRoom(state)?.variables || [],
    shallowEqual
  );
  useEffect(() => {
    if (open) {
      reset({ roomVariables: variables });
    }
  }, [open, variables, reset]);

  const onClose = useCallback(async () => {
    if (!(await trigger())) {
      return;
    }

    const roomVariables = getValues().roomVariables.filter(
      (v) => v.label.length > 0 && v.value.length > 0
    );
    dispatch(updateCurrentRoom({ variables: roomVariables }));
    dispatch(
      appStateMutate((state) => {
        state.openRoomVariables = false;
      })
    );
  }, [dispatch, getValues, trigger]);

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const oldIndex = e.active?.data.current?.sortable.index;
      const newIndex = e.over?.data.current?.sortable.index;
      if (oldIndex === undefined || newIndex === undefined) return;

      move(oldIndex, newIndex);
    },
    [move]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <AppBar color="default" position="sticky">
        <Toolbar>
          <Typography style={{ flex: 1 }} fontSize="20px" fontWeight="bold">
            {t("ルーム変数を編集")}
          </Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            size="large"
            aria-label={t("閉じる")}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Toolbar variant="dense" disableGutters>
          <StyledTitle variant="subtitle2">{t("ルーム変数")}</StyledTitle>
          <IconButton
            onClick={() => remove(fields.length - 1)}
            disabled={fields.length < 1}
            size="small"
            aria-label={t("ルーム変数を削除")}
          >
            <RemoveIcon />
          </IconButton>
          <IconButton
            onClick={() => append({ label: "", value: "" })}
            disabled={fields.length >= MAX_VARIABLES}
            size="small"
            edge="end"
            aria-label={t("ルーム変数を追加")}
          >
            <AddIcon />
          </IconButton>
        </Toolbar>
        <Typography
          variant="caption"
          display="block"
          style={{ marginBottom: 16 }}
        >
          {t(
            "ルームに変数を設定します。{ラベル名}のように発言するとチャットから値を参照することができます。"
          )}
        </Typography>
        <div style={{ isolation: "isolate" }}>
          <DndContext
            sensors={sensors}
            onDragEnd={onDragEnd}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext items={fields}>
              {fields.map((field, index) => {
                const errorMessageLabel =
                  errors.roomVariables &&
                  errors.roomVariables[index]?.label?.message;
                const errorMessageValue =
                  errors.roomVariables &&
                  errors.roomVariables[index]?.value?.message;
                return (
                  <DraggableValiable
                    key={field.id}
                    id={field.id}
                    index={index}
                    errorMessageLabel={errorMessageLabel}
                    errorMessageValue={errorMessageValue}
                    register={register}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" fullWidth onClick={onClose}>
          {t("閉じる")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type DraggableValiableProps = {
  id: string;
  index: number;
  errorMessageLabel: string | undefined;
  errorMessageValue: string | undefined;
  register: any;
};

const DraggableValiable = ({
  id,
  index,
  errorMessageLabel,
  errorMessageValue,
  register,
}: DraggableValiableProps) => {
  const [t] = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    active,
  } = useSortable({
    id,
  });

  const grab = { cursor: isDragging ? "grabbing" : "grab" };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    pointerEvent: active && !isDragging ? "none" : undefined,
    zIndex: isDragging ? 999 : undefined,
    ...grab,
  };

  return (
    <StyledGroup
      ref={setNodeRef}
      $hoverColor={isDragging ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.2)"}
      style={style}
      {...attributes}
      {...listeners}
    >
      <TextField
        variant="standard"
        label={t("ラベル")}
        margin="dense"
        fullWidth
        error={errorMessageLabel != null}
        helperText={errorMessageLabel || (errorMessageValue && " ")}
        {...register(`roomVariables.${index}.label`, {
          maxLength: {
            value: 16,
            message: t("16文字以下としてください"),
          },
        })}
        InputLabelProps={{
          sx: {
            ...grab,
          },
        }}
        sx={{
          pointerEvents: active ? "none" : undefined,
        }}
      />
      <TextField
        variant="standard"
        label={t("値")}
        margin="dense"
        fullWidth
        error={errorMessageValue != null}
        helperText={errorMessageValue || (errorMessageLabel && " ")}
        {...register(`roomVariables.${index}.value`, {
          maxLength: {
            value: 64,
            message: t("64文字以下としてください"),
          },
        })}
        InputLabelProps={{
          sx: {
            ...grab,
          },
        }}
        sx={{
          pointerEvents: active ? "none" : undefined,
        }}
      />
    </StyledGroup>
  );
};

const StyledGroup = styled.div<{ $hoverColor: string }>`
  position: relative;
  display: flex;
  align-items: center;
  .MuiTextField-root {
    flex: 1;
  }
  > * + * {
    margin-left: 16px;
  }
  .MuiAvatar-root {
    background: rgba(0, 0, 0, 0.2);
    img {
      object-position: 50% 0%;
    }
  }
  &:hover {
    background: ${(props) => props.$hoverColor};
    box-shadow: 8px 0px ${(props) => props.$hoverColor},
      -8px 0px ${(props) => props.$hoverColor};
  }
`;
const StyledTitle = styled(Typography)`
  flex-grow: 1;
`;

export default memo(RoomVariablesDialog);
